import React from 'react';

// Regex qui capture les URLs http(s):// et www. — tolérant sur les caractères URL classiques.
// On évite les caractères de fin de phrase (., !, ?, ,, ;) et les parenthèses/crochets déséquilibrés.
const URL_REGEX = /(\b(?:https?:\/\/|www\.)[^\s<>"']+[^\s<>"'.,;:!?)\]}])/gi;

/**
 * Transforme un texte brut en fragments JSX en rendant les URLs cliquables.
 * @param {string} text - Texte source
 * @param {(url: string, index: number) => string} [makeKey] - Optionnel, pour éviter les warnings React.
 * @returns {Array<string|JSX.Element>}
 */
export function linkifyText(text, makeKey) {
    if (!text || typeof text !== 'string') return [text];
    const parts = [];
    let last = 0;
    let m;
    let idx = 0;
    URL_REGEX.lastIndex = 0;
    while ((m = URL_REGEX.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const raw = m[0];
        const href = raw.startsWith('www.') ? `https://${raw}` : raw;
        const key = makeKey ? makeKey(raw, idx) : `lnk-${idx}-${m.index}`;
        parts.push(
            <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2 break-all"
                onClick={(e) => e.stopPropagation()}
                data-testid="msg-link"
            >
                {raw}
            </a>
        );
        idx += 1;
        last = m.index + raw.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? parts : [text];
}

/**
 * Composant : rend un texte simple avec URLs cliquables + support @mentions optionnel.
 * Ne casse pas les retours à la ligne (whitespace-pre-wrap doit être appliqué par le parent).
 */
export function Linkify({ text, mentionNames = [], className = '', mentionClassName = 'bg-amber-100 text-amber-800 rounded px-1 font-semibold', testId }) {
    if (!text) return null;
    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // On tokenise d'abord par mentions (si fournies), puis on linkifie chaque segment texte.
    let segments;
    if (mentionNames && mentionNames.length > 0) {
        const pattern = new RegExp(`@(${mentionNames.map(escapeRegex).join('|')})\\b`, 'g');
        segments = [];
        let last = 0;
        let m;
        let i = 0;
        while ((m = pattern.exec(text)) !== null) {
            if (m.index > last) segments.push({ type: 'text', value: text.slice(last, m.index), key: `t-${i}` });
            segments.push({ type: 'mention', value: m[0], key: `mn-${i}` });
            last = m.index + m[0].length;
            i += 1;
        }
        if (last < text.length) segments.push({ type: 'text', value: text.slice(last), key: `t-${i}` });
    } else {
        segments = [{ type: 'text', value: text, key: 't-0' }];
    }

    return (
        <span className={className} data-testid={testId}>
            {segments.map((seg) => {
                if (seg.type === 'mention') {
                    return (
                        <span key={seg.key} className={mentionClassName} data-testid="pw-mention-tag">
                            {seg.value}
                        </span>
                    );
                }
                return (
                    <React.Fragment key={seg.key}>
                        {linkifyText(seg.value, (u, i) => `${seg.key}-lnk-${i}`)}
                    </React.Fragment>
                );
            })}
        </span>
    );
}
